import React from 'react'
import { graphql } from 'gatsby'
import { Helmet } from 'react-helmet'
import Img from 'gatsby-image'
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components'
import { theme } from '../utils/theme'
import baseStyles from '../utils/theme'
import Logo from '../components/Logo'

const GlobalStyle = (createGlobalStyle as any)`
  ${baseStyles}
  body {
    margin: 0;
    padding: 0;
    background-color: #000;
  }
`

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'Gilroy', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`

const HeroSection = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: relative;
  color: #fff;
  padding: 4rem 2rem;
  overflow: hidden;
`

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    background: radial-gradient(circle, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.8) 100%);
  }

  .gatsby-image-wrapper {
    height: 100%;
  }
`

const Content = styled.div`
  position: relative;
  z-index: 1;
  max-width: 900px;
  margin: 0 auto;
`

const Title = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 2.5rem;
  line-height: 1.1;
  color: #fff;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`

const Subtitle = styled.div`
  font-size: 1.25rem;
  line-height: 1.6;
  margin-bottom: 3.5rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  
  p {
    margin-bottom: 1.5rem;
    max-width: 750px;
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`

const StyledButton = styled.a`
  display: inline-block;
  background-color: ${(props: any) => props.theme.colors.primary};
  color: ${(props: any) => props.theme.colors.light};
  padding: 1.25rem 2.5rem;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  text-decoration: none;
  
  &:hover {
    background-color: #fff;
    color: ${(props: any) => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.2);
  }
`

const Footer = styled.footer`
  background-color: #000;
  padding: 2rem;
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
  }
`

const Copyright = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin: 0;
`

const LogoContainer = styled.div`
  svg {
    height: 50px;
    width: auto;
    overflow: visible;
  }
`

const GoodplanPage = ({ data }: any) => {
  return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        <GlobalStyle />
        <Helmet>
          <title>Parinity’s Transition to Goodplan</title>
          <meta name="description" content="Parinity is transitioning its financial advice services to Goodplan." />
        </Helmet>
        
        <HeroSection>
          {data.backgroundImage && (
            <BackgroundImage>
              <Img 
                fluid={data.backgroundImage.childImageSharp.fluid} 
                style={{ height: '100%' }}
                imgStyle={{ objectFit: 'cover' }}
              />
            </BackgroundImage>
          )}
          
          <Content>
            <Title>Parinity’s Transition to Goodplan</Title>
            <Subtitle>
              <p>
                For almost 15 years Parinity has provided quality bespoke financial advice to a broad range of clients from all walks of life.
              </p>
              <p>
                However just like a financial plan, things can change, and it's now time to pass the baton over to Zach Webber at Goodplan to continue to provide the quality financial advice services Parinity clients have come to expect.
              </p>
              <p>
                Thank you to all the clients of Parinity for your trust and loyalty, I will always be grateful.
              </p>
            </Subtitle>
            
            <StyledButton href="https://www.goodplan.com.au/">
              Zach Webber – Goodplan
            </StyledButton>
          </Content>
        </HeroSection>
        
        <Footer>
          <LogoContainer>
            <Logo color="light" />
          </LogoContainer>
          <Copyright>
            © {new Date().getFullYear()} Parinity Pty Ltd. All rights reserved.
          </Copyright>
          <div style={{ width: '186px' }} />
        </Footer>
      </PageContainer>
    </ThemeProvider>
  )
}

export default GoodplanPage

export const query = graphql`
  query GoodplanPageQuery {
    backgroundImage: file(relativePath: { eq: "cta-bg.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 3000, quality: 95) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
